import React, { useMemo, useState, useEffect } from "react";
import "../styles/businessGroups.css";
import "../styles/pageConfiguration.css";
import "../styles/toast.css";
import { Text, InputCheckbox, InputFieldV2, InputDropdown, Button, Modal, Icon, ActionButton } from "../custom-components";
import { IcClose, IcTrash, IcSort, IcGroup, IcSearch, IcRefresh, IcDownload, IcEditPen, IcFilter, IcInfo } from "../custom-components/Icon";
import { BsSearch } from "react-icons/bs";
import { Slide, ToastContainer, toast } from "react-toastify";
import CustomToast from "./CustomToastContainer";
import { useDispatch, useSelector } from "react-redux";
import { createBusiness, fetchBusinessApplications } from "../store/actions/CommonAction";
import { formatToIST } from "../utils/dateUtils";

const BusinessGroups = () => {

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const nameCharLimit = 250;
  const dispatch = useDispatch();
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [search, setSearch] = useState("");


  const handleOpenModal = () => {
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const [text, setText] = useState("");
  const [charError, setCharError] = useState("");
  const charLimit = 300;

  const handleTextAreaChange = (e) => {
    const inputValue = e.target.value;

    if (inputValue.length <= charLimit) {
      setText(inputValue);
      // Show error when limit is reached
      if (inputValue.length === charLimit) {
        setCharError("Maximum 300 characters allowed.");
      } else {
        setCharError("");
      }
    } else {
      // Prevent typing beyond limit
      setText(inputValue.slice(0, charLimit));
      setCharError("Maximum 300 characters allowed.");
    }
  };

  const handleNameChange = (e) => {
    const inputValue = e.target.value;

    if (inputValue.length <= nameCharLimit) {
      setName(inputValue);
      // Show error when limit is reached
      if (inputValue.length === nameCharLimit) {
        setNameError("Maximum 250 characters allowed.");
      } else {
        setNameError("");
      }
    } else {
      // Prevent typing beyond limit
      setName(inputValue.slice(0, nameCharLimit));
      setNameError("Maximum 250 characters allowed.");
    }
  };

  const [createBusinessGroup, setCreateBusinessGroup] = useState(false);

  const tenantId = useSelector((state) => state.common.tenant_id);
  const token = useSelector((state) => state.common.session_token);

  const resetForm = () => {
    setName("");
    setText("");
    setNameError("");
    setCharError("");
  };

  const closeBusinessGroupModal = () => {
    resetForm();
    setCreateBusinessGroup(false);
  }

  const handleSubmit = async () => {
    // alert("Creating business group...");


    if (!name.trim()) {
      toast.error(
        (props) => (
          <CustomToast
            {...props}
            type="error"
            message={"Please fill name."}
          />
        ),
        { icon: false }
      );
      return;
    }

    if (name.length > nameCharLimit) {
      toast.error(
        (props) => (
          <CustomToast
            {...props}
            type="error"
            message={"Maximum 250 characters allowed for name."}
          />
        ),
        { icon: false }
      );
      return;
    }

    if (text.length > charLimit) {
      toast.error(
        (props) => (
          <CustomToast
            {...props}
            type="error"
            message={"Maximum 300 characters allowed."}
          />
        ),
        { icon: false }
      );
      return;
    }

    if (!text.trim()) {
      toast.error(
        (props) => (
          <CustomToast
            {...props}
            type="error"
            message={"Please fill description."}
          />
        ),
        { icon: false }
      );
      return;
    }

    try {
      const payload = {
        name: name.trim(),
        description: text.trim(),
      };

      const response = await createBusiness(payload, token, tenantId);

      if (response?.businessId) {
        toast.success(
          (props) => (
            <CustomToast
              {...props}
              type="success"
              message={"Business unit created successfully!"}
            />
          ),
          { icon: false }
        );

        resetForm();
        setCreateBusinessGroup(false);
        dispatch(fetchBusinessApplications(token, tenantId));
        return;
      }

      if (Array.isArray(response) && response[0]?.errorMessage) {
        toast.error(
          (props) => (
            <CustomToast
              {...props}
              type="error"
              message={response[0].errorMessage}
            />
          ),
          { icon: false }
        );
        return;
      }

      toast.error(
        (props) => (
          <CustomToast
            {...props}
            type="error"
            message={"Failed to create business unit. Please try again."}
          />
        ),
        { icon: false }
      );

    } catch (err) {
      console.error("Error creating business group:", err);
      const errorMessage = err?.[0]?.errorMessage || err?.errorMessage || "Failed to create business unit. Please try again.";

      toast.error(
        (props) => (
          <CustomToast
            {...props}
            type="error"
            message={errorMessage}
          />
        ),
        { icon: false }
      );
    }

  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const formatted = formatToIST(dateStr);
    if (formatted === 'N/A') return "-";
    // Extract just the date part (DD-MM-YYYY)
    return formatted.split(' ')[0]; // Get "DD-MM-YYYY" from "DD-MM-YYYY HH:mm:ss"
  };

  useEffect(() => {
    if (!tenantId || !token) return;
    dispatch(fetchBusinessApplications(token, tenantId));
  }, [dispatch, tenantId, token]);

  const businesses = useSelector((state) => state.common.businesses);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (column) => {
    return <Icon ic={<IcSort />} size="small" color="black" />;
  };

  const sortedBusinesses = useMemo(() => {
    if (!sortColumn) return businesses;

    return [...businesses].sort((a, b) => {
      const aValue = a[sortColumn] || '';
      const bValue = b[sortColumn] || '';

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [businesses, sortColumn, sortDirection]);

  return (
    <>
      <div className="configurePage">
        <div className="bs-page">
          <div className="bs-header-container">
            <div className="bs-header-left">
              <div className="bs-header-icon-container">
                <Icon ic={<IcGroup height={24} width={24} />} color="primary_60" />
              </div>
              <div className="bs-header-text inline-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text appearance="heading-s" color="primary-grey-100">
                  Business Units
                </Text>
                <div className="custom-header-tooltip-container" style={{ display: 'flex' }}>
                  <div className="custom-header-tooltip-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                  </div>
                  <div className="custom-header-tooltip-text" style={{ width: '280px', fontWeight: 'normal' }}>
                    Manage business units to organize users, permissions and data access within the consent management system.
                  </div>
                </div>
              </div>
            </div>
            <div className="bs-header-right" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button
                onClick={() => setCreateBusinessGroup(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#3B82F6', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(59,130,246,0.3)', whiteSpace: 'nowrap' }}
              >
                + Create Business Unit
              </button>
            </div>
          </div>

          <div className="bs-content">
            <div className="business-table-card">
              <div className="business-table-container">
                <table className="business-table">
                  <thead>
                    <tr>
                      <th style={{ width: '5%' }}>#</th>
                      <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>Business Unit Name</span>
                          {renderSortIcon('name')}
                        </div>
                      </th>
                      <th onClick={() => handleSort('description')} style={{ cursor: 'pointer' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>Description</span>
                          {renderSortIcon('description')}
                        </div>
                      </th>
                      <th onClick={() => handleSort('createdAt')} style={{ cursor: 'pointer' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>Created On</span>
                          {renderSortIcon('createdAt')}
                        </div>
                      </th>
                      <th>
                        <span>Status</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedBusinesses && sortedBusinesses.length > 0 ? (
                      sortedBusinesses.map((biz, index) => (
                        <tr key={biz.businessId}>
                          <td>{index + 1}</td>
                          <td style={{ fontWeight: 600 }}>{biz.name || "-"}</td>
                          <td>{biz.description || "-"}</td>
                          <td>{formatDate(biz.createdAt)}</td>
                          <td>
                            <span className="status-badge active">
                              <span className="status-dot"></span> Active
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" style={{ textAlign: "center", padding: "20px" }}>
                          No business applications found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="business-pagination">
                <Text appearance="body-xs" color="primary-grey-60">
                  Showing 1 to {sortedBusinesses?.length || 0} of {sortedBusinesses?.length || 0} business units
                </Text>
                <div className="pagination-controls">
                  <button className="page-btn disabled">&lt;</button>
                  <button className="page-btn active">1</button>
                  <button className="page-btn disabled">&gt;</button>
                </div>
              </div>
            </div>
          </div>

          {createBusinessGroup && (
            <div className="modal-outer-container">
              <div className="modal-container-old" style={{ height: '410px' }}>
                <div className="modal-close-btn-container">
                  <ActionButton
                    onClick={closeBusinessGroupModal}
                    icon={<IcClose />}
                    kind="tertiary"
                    size="small"
                  ></ActionButton>
                </div>
                <Text appearance="heading-xs" color="primary-grey-100">

                  Add business unit
                </Text>
                <br></br>
                <div style={{ marginTop: "10px" }}>
                  <InputFieldV2
                    label="Name (Required)"
                    size="small"
                    value={name}
                    onChange={(e) => handleNameChange(e)}
                    maxLength={nameCharLimit}
                    state={nameError ? "error" : "none"}
                    stateText={nameError || ""}
                  />
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "4px" }}>
                    <Text appearance="body-xs" color={name.length === nameCharLimit ? "error" : "primary-grey-80"} style={{ fontSize: "12px" }}>
                      {name.length}/{nameCharLimit}
                    </Text>
                  </div>
                </div>
                <br></br>
                <Text appearance="body-xs" color="primary-grey-80">
                  Description (Required)
                </Text>

                <textarea
                  placeholder="Enter description of the group's function or scope."
                  value={text}
                  onChange={handleTextAreaChange}
                  maxLength={charLimit}
                  rows="4"
                  className="custom-text-area"
                />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "4px" }}>
                  <div>
                    {charError && (
                      <Text appearance="body-xs" color="error" style={{ fontSize: "12px" }}>
                        {charError}
                      </Text>
                    )}
                  </div>
                  <div className="char-counter" style={{ color: text.length === charLimit ? "#f44336" : "#666" }}>
                    {text.length}/{charLimit}
                  </div>
                </div>
                <div className="modal-add-btn-container">

                  <ActionButton label="Create" onClick={handleSubmit}></ActionButton>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
      <ToastContainer
        position="bottom-left"
        autoClose={3000}
        hideProgressBar
        closeOnClick
        pauseOnHover
        draggable
        closeButton={false}
        toastClassName={() => "toast-wrapper"}
        transition={Slide}
      />
    </>
  );

}


export default BusinessGroups;